// ===========================================
// Attendance Report Filters Component
// ===========================================
// Filter form for attendance reports with term, date range, lesson, teacher, and student filters

import { useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Box,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AppDayjsLocalizationProvider } from '../common/AppDayjsLocalizationProvider';
import { FilterList as FilterIcon, Close as ClearIcon } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';

import { AttendanceReportFilters } from '../../api/reports.api';
import { useTerms } from '../../hooks/useAdmin';
import { useLessons } from '../../hooks/useLessons';
import { useTeachers, useStudents } from '../../hooks/useUsers';

// ===========================================
// TYPES
// ===========================================

interface AttendanceFiltersProps {
  filters: AttendanceReportFilters;
  onFiltersChange: (filters: AttendanceReportFilters) => void;
  onApply: () => void;
  onReset: () => void;
}

// ===========================================
// COMPONENT
// ===========================================

export default function AttendanceFilters({
  filters,
  onFiltersChange,
  onApply,
  onReset,
}: AttendanceFiltersProps) {
  // Local state for date pickers (using dayjs)
  const [startDate, setStartDate] = useState<Dayjs | null>(
    filters.startDate ? dayjs(filters.startDate) : null
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    filters.endDate ? dayjs(filters.endDate) : null
  );

  // Fetch filter options
  const { data: terms = [] } = useTerms();
  const { data: lessons = [] } = useLessons();
  const { data: teachers = [] } = useTeachers();
  const { data: students = [] } = useStudents();

  // Filter to active terms only
  const activeTerms = terms.filter((term) => term.isActive);

  const handleFieldChange = (field: keyof AttendanceReportFilters, value: string | undefined) => {
    onFiltersChange({ ...filters, [field]: value || undefined });
  };

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue);
    onFiltersChange({
      ...filters,
      startDate: newValue ? newValue.format('YYYY-MM-DD') : undefined,
    });
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    setEndDate(newValue);
    onFiltersChange({
      ...filters,
      endDate: newValue ? newValue.format('YYYY-MM-DD') : undefined,
    });
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    onReset();
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <AppDayjsLocalizationProvider>
          <Grid container spacing={2}>
            {/* Term Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Term"
                value={filters.termId || ''}
                onChange={(e) => handleFieldChange('termId', e.target.value)}
                size="small"
              >
                <MenuItem value="">All Terms</MenuItem>
                {activeTerms.map((term) => (
                  <MenuItem key={term.id} value={term.id}>
                    {term.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                minDate={startDate || undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>

            {/* Lesson/Class Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Class"
                value={filters.lessonId || ''}
                onChange={(e) => handleFieldChange('lessonId', e.target.value)}
                size="small"
              >
                <MenuItem value="">All Classes</MenuItem>
                {lessons.map((lesson) => (
                  <MenuItem key={lesson.id} value={lesson.id}>
                    {lesson.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Teacher Filter (reports are school-wide: roles slice 1a) */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Teacher"
                value={filters.teacherId || ''}
                onChange={(e) => handleFieldChange('teacherId', e.target.value)}
                size="small"
              >
                <MenuItem value="">All Teachers</MenuItem>
                {teachers.map((teacher: any) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    {teacher.firstName} {teacher.lastName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Student Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Student"
                value={filters.studentId || ''}
                onChange={(e) => handleFieldChange('studentId', e.target.value)}
                size="small"
              >
                <MenuItem value="">All Students</MenuItem>
                {students.map((student: any) => (
                  <MenuItem key={student.id} value={student.id}>
                    {student.firstName} {student.lastName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} sm={6} md={6}>
              <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<FilterIcon />}
                  onClick={onApply}
                  fullWidth
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleReset}
                  fullWidth
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </AppDayjsLocalizationProvider>
      </CardContent>
    </Card>
  );
}
